import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ListItem, ProgressIndicator, IconDocument } from '@veneer/core';
import ModalError, { ModalErrorDetail } from '../../components/Modals/Error';
import HeaderBar from '../../components/HeaderBar';
import { ScreenInfo } from '../../jarvis/dataCollection';
import './index.css';

import type { OrdersData, OrdersItem } from '../../types';
import type { AxiosResponse } from 'axios';
import { usePhotobookApiWebHttpClient } from '../../helpers/useJarvisApiWebHttpClient';
import { PhotobookApi } from '../../helpers/apis/photobookApi';

declare global {
  interface Window {
    launchMediaclipDesigner: (projectId: string, userMediaclipToken: string, keepAliveUrl: string) => void;
  }
}

const OrdersPage = () => {
  const [loading, setLoading] = useState(true);
  const [ordersList, setOrdersList] = useState([{} as OrdersItem]);
  const [hasError, setHasError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<ModalErrorDetail>();

  const history = useHistory();

  const screenInfo: ScreenInfo = { name: 'Orders', activity: 'OrderPhotobook-v01' };

  const photobookApi = new PhotobookApi(usePhotobookApiWebHttpClient());

  useEffect(() => {
    listOrders();
  }, []);

  const listOrders = async () => {
    try {
      const response: AxiosResponse = await photobookApi.getOrders();
      if (!response.data) throw new Error(`Unable to load orders response`);
      handleOrderData(response.data);
    } catch (error) {
      setLoading(false);
      setHasError(true);
      setErrorDetail({
        context: 'Error obtaining list of orders',
        ...(error instanceof Error && { error: error }),
      });
      console.log(error);
    }
  };

  const handleOrderData = (data: OrdersData) => {
    const { orders } = data;
    setOrdersList(orders);
    setLoading(false);
  };

  const onClickBack = () => {
    history.push('/');
  };

  if (hasError) {
    return <ModalError state={hasError} detail={errorDetail as ModalErrorDetail} screenInfo={screenInfo} />;
  }

  if (loading) {
    return (
      <div className="spinner">
        <ProgressIndicator />
      </div>
    );
  }

  return (
    <section className="ordersPage">
      <HeaderBar handleBack={onClickBack} title="HP Smart" />
      <div id="designer" className="mediaclip-designer-wrapper">
        {ordersList.length !== 0 ? (
          ordersList.map((order, index) => (
            <div key={`order-${order.id}`}>
              <ListItem
                className="orderListItem"
                leadingArea={<IconDocument color="colorHpBlue6" />}
                centerArea={
                  <div className="orderListItemCenter">
                    <p className="vn-list-item__title">Order #{ordersList.length - index}</p>
                    <p className="vn-list-item__title">{new Date(order.dateCreatedUtc).toLocaleString('en-US')}</p>
                  </div>
                }
              />
              <div className="orderItemContent">
                <div className="valueRow">
                  <b>Order Id</b>
                  <span>{order.id}</span>
                </div>
                {order.lines.map((line, index) => (
                  <div className="valueBlock" key={`order-line-${index}`}>
                    <div className="valueRow">
                      <b>Project Id</b>
                      <span>{line.projectId}</span>
                    </div>
                    {line.status && (
                      <>
                        <div className="valueRow">
                          <b>Status</b>
                          <span>{line.status.value}</span>
                        </div>
                        <div className="valueRow">
                          <b>Flags</b>
                          <span>{line.status.flags}</span>
                        </div>
                        <div className="valueRow">
                          <b>Effective Date</b>
                          <span>{new Date(line.status.effectiveDateUtc).toLocaleString('en-US')}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <p>No orders found.</p>
        )}
      </div>
    </section>
  );
};

export default OrdersPage;
